export interface I18n {
  global: {
    popStackButtonLabel: string;
    poiCountSingular: string;
    poiCountPlural: string;
    closeLabel: string;
  },
  poiDetails: {
    general: {
      title: string;
      customizeImage: string;
      ratingsDescription: string;
    };
    description: {
      addDescription: string;
      editDescription: string;
    }
    location: {
      title: string;
      centerInMap: string;
      copyAddress: string;
      copyAddressConfirmation: string;
    }
    links: {
      title: string;
      addLink: string;
      copyPhoneConfirmation: string;
      callPhoneNumber: string;
      openLink: string;
      openLinkPopup: string;
      copyWebsiteConfirmation: string;
      customLinkOptions: {
        copyToClipboard: string;
        editLink: string;
        deleteLink: string;
      };
    };
    schedule: {
      title: string;
      describeTransit: string;
      describeDuration: string;
      temporarilyClosed: string;
      permanentlyClosed: string;
      currentlyClosed: string;
      currentlyOpen: string;
    },
    openingHours: {
      title: string;
    },
    actions: {
      title: string;
      deletePoi: string;
      renamePoi: string;
    },
    poiCategory: {
      'title': string;
      'popularCategories': string;
      'usageCount': string;
    },
    poiGroup: {
      'title': string;
      'highlightInItinerary': string;
      'highlightInItineraryDetails': string;
    },
    routeEffect: {
      'title': string;
    },
    metaDataErrorMessage: string;
  },
  itemDecorations: {
    'chevron': string;
    'copy': string;
    'edit': string;
    'ellipsis': string;
    'externalLink': string;
    'optionSelected': string;
    'optionEmpty': string;
    'popupmenu': string;
  },
  poiCategoryGroups: { [category: string]: string },
  poiCategories: { [category: string]: string },
  routeEffects: {
    none: RouteEffectI18n,
    continue: RouteEffectI18n,
    restart: RouteEffectI18n
  }
}

export interface RouteEffectI18n {
  label: string;
  description: string;
}

export const i18n = (): I18n => {
  return {
    global:            {
      popStackButtonLabel: 'Close',
      poiCountSingular:    'One point of interest',
      poiCountPlural:      '%count% points of interest',
      closeLabel:          'Close'
    },
    poiDetails:        {
      general:              {
        title:              'General',
        customizeImage:     'Customize image',
        ratingsDescription: 'Rated %rating% based on %count% reviews'
      },
      description:          {
        addDescription:  'Add custom description',
        editDescription: 'Edit description'
      },
      location:             {
        title:                   'Location',
        centerInMap:             'Center in map',
        copyAddress:             'Copy address',
        copyAddressConfirmation: 'Address copied to clipboard'
      },
      links:                {
        title:                   'Links',
        addLink:                 'Add link',
        copyPhoneConfirmation:   'Phone number copied to clipboard',
        openLink:                'Open link',
        openLinkPopup:           'Show available link interactions',
        callPhoneNumber:         'Call phone number',
        copyWebsiteConfirmation: 'Website copied to clipboard',
        customLinkOptions:       {
          copyToClipboard: 'Copy to clipboard',
          editLink:        'Edit link',
          deleteLink:      'Delete link'
        }
      },
      schedule:             {
        title:             'Schedule',
        describeTransit:   'Describe how to get here',
        describeDuration:  'Describe duration',
        currentlyClosed:   'Currently closed',
        currentlyOpen:     'Currently open',
        permanentlyClosed: 'Permanently closed',
        temporarilyClosed: 'Temporarily closed'
      },
      openingHours:         {
        title: 'Opening hours'
      },
      actions:              {
        title:     'Actions',
        deletePoi: 'Delete point of interest from trip',
        renamePoi: 'Rename point of interest'
      },
      poiCategory:          {
        title:             'Category',
        popularCategories: 'Popular in this trip',
        usageCount:        'Used %count% times in this trip'
      },
      poiGroup:             {
        title:                       'Group',
        highlightInItinerary:        'Highlight in itinerary',
        highlightInItineraryDetails: 'Visually highlight this point of interest in the itinerary view. Useful for finding POIs in long itineraries.'
      },
      routeEffect:          {
        title: 'Map display'
      },
      metaDataErrorMessage: 'There was an problem loading additional information for this point of interest, so some details may be missing.'
    },
    itemDecorations:   {
      chevron:        'Expand',
      copy:           'Copy to clipboard',
      edit:           'Edit',
      ellipsis:       'Ellipsis',
      externalLink:   'Open external link in new tab',
      optionSelected: 'Selected option',
      optionEmpty:    'Unselected option',
      popupmenu:      'Open popup menu with more options'
    },
    routeEffects:      {
      none:     {
        label:       'Off-route',
        description: 'Marker will not be connected to neighbouring markers in the map.'
      },
      continue: {
        label:       'On-route',
        description: 'Marker will be connected to the previous on-route marker in the map.'
      },
      restart:  {
        label:       'Start new route',
        description: 'Marker will start a new route in the map that following markers can be connected to. It will not be connected to the previous on-route marker.'
      }
    },
    poiCategoryGroups: {
      'GENERAL':        'General',
      'ACTIVITY':       'Activities',
      'GEOGRAPHY':      'Geography',
      'CULINARY':       'Culinary',
      'TRANSPORTATION': 'Transportation'
    },
    poiCategories:     {
      'GENERIC':                   'General point of interest',
      'ACCOMMODATION':             'Accommodation',
      'LANDMARK':                  'Landmark',
      'VISTAPOINT':                'Vista point',
      'PHOTOOPPORTUNITY':          'Photo opportunity',
      'GEOGRAPHY_CITY':            'City',
      'GEOGRAPHY_CAPITAL':         'Capital',
      'GEOGRAPHY_REGION':          'Region',
      'ACTIVITY_SHOPPING':         'Shopping',
      'ACTIVITY_NATURE':           'Nature / Outdoors',
      'ACTIVITY_WILDLIFE':         'Wildlife',
      'ACTIVITY_WALKING':          'Walking / Hiking',
      'ACTIVITY_SPORT':            'Sport',
      'ACTIVITY_WINTERSPORTS':     'Winter sports',
      'ACTIVITY_MUSIC':            'Music',
      'ACTIVITY_CULTURE':          'Art and culture',
      'ACTIVITY_MUSEUM':           'Museum',
      'ACTIVITY_EVENT':            'Event',
      'ACTIVITY_DRIVE':            'Scenic drive',
      'ACTIVITY_RELAX':            'Relaxation',
      'ACTIVITY_SPA':              'Wellness',
      'CULINARY_FOOD':             'Restaurant',
      'CULINARY_COFFEE':           'Café',
      'CULINARY_BAR':              'Bar',
      'CULINARY_PUB':              'Pub',
      'CULINARY_NIGHTLIFE':        'Nightlife',
      'TRANSPORTATION_PLANE':      'Air travel',
      'TRANSPORTATION_TRAIN':      'Rail travel',
      'TRANSPORTATION_CAR':        'Car travel',
      'TRANSPORTATION_BUS':        'Public transport',
      'TRANSPORTATION_MOTORCYCLE': 'Motorcycle',
      'TRANSPORTATION_BICYCLE':    'Bicycle',
      'TRANSPORTATION_SHIP':       'Ship / Ferry'
    }
  };
};
